import { apiClient } from '../utils/axios-config';
import { User } from '../types';
import { Permission, UserRole } from '../types/permissions.types';

export interface UserProfile {
  user: User;
  permissions: Permission[];
  userRole: UserRole;
  tenant: {
    id: string;
    name: string;
    settings: any;
  };
  lastSync: number;
}

export interface AuthProfileApi {
  /**
   * Obtener perfil completo del usuario autenticado
   * Incluye: datos del usuario, permisos actualizados, configuración del tenant
   */
  getProfile(): Promise<UserProfile>;
  
  /**
   * Verificar si el token actual es válido
   */
  verifyToken(): Promise<{ valid: boolean; user?: User }>;
  
  /**
   * Actualizar perfil del usuario
   */
  updateProfile(profileData: Partial<User>): Promise<User>;
  
  /**
   * Cambiar contraseña
   */
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  
  /**
   * Obtener configuración del tenant
   */
  getTenantConfig(): Promise<any>;
}

const authProfileApi: AuthProfileApi = {
  /**
   * Obtener perfil completo del usuario autenticado
   * Este endpoint es clave para la sincronización de permisos
   */
  async getProfile(): Promise<UserProfile> {
    try {
      const response = await apiClient.get('/auth/me');
      
      // El backend responde con esta estructura desde /auth/me (envuelta por ResponseTransformInterceptor):
      // {
      //   success: true,
      //   data: {
      //     user: { id, email, role, tenantId, tenant, status, firstName, lastName, fullName, profile, createdAt },
      //     redirectUrl: "..."
      //   }
      // }
      
      // Extraer los datos del usuario desde la respuesta envuelta
      const responseData = response.data?.data || response.data;

      if (!responseData || typeof responseData !== 'object') {
        throw new Error('Respuesta de perfil inválida');
      }

      const userData = (responseData as any).user || responseData;

      if (!userData || typeof userData !== 'object') {
        throw new Error('Datos de usuario no disponibles');
      }

      const role = userData.role || (responseData as any).role;
      if (!role) {
        throw new Error('Rol de usuario no definido');
      }
      
      return {
        user: {
          id: userData.id,
          email: userData.email,
          role,
          tenantId: userData.tenantId || (responseData as any).tenantId,
          tenant: userData.tenant || (responseData as any).tenant,
          firstName: userData.firstName,
          lastName: userData.lastName,
          profile: userData.profile,
          createdAt: userData.createdAt,
          updatedAt: userData.updatedAt || userData.createdAt,
          isActive: userData.status ? userData.status === 'active' : true,
          onboardingCompleted:
            userData.onboardingCompleted ?? (responseData as any).onboardingCompleted ?? false,
        },
        permissions: (responseData as any).permissions || [],
        userRole: role,
        tenant: {
          id: userData.tenantId || (responseData as any).tenantId || '',
          name:
            (responseData as any).tenant?.name ||
            userData.tenant ||
            (responseData as any).tenant ||
            'Tenant',
          settings:
            (responseData as any).tenant?.settings ||
            (responseData as any).settings ||
            {},
        },
        lastSync: Date.now(),
      };
    } catch (error) {
      throw new Error('No se pudo obtener el perfil del usuario');
    }
  },

  /**
   * Verificar si el token actual es válido
   */
  async verifyToken(): Promise<{ valid: boolean; user?: User }> {
    try {
      const response = await apiClient.get('/auth/verify');
      return {
        valid: true,
        user: response.data.user,
      };
    } catch (error) {
      return {
        valid: false,
      };
    }
  },

  /**
   * Actualizar perfil del usuario
   */
  async updateProfile(profileData: Partial<User>): Promise<User> {
    try {
      const response = await apiClient.put('/auth/profile', profileData);
      return response.data;
    } catch (error) {
      throw new Error('No se pudo actualizar el perfil');
    }
  },

  /**
   * Cambiar contraseña
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      await apiClient.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
    } catch (error) {
      throw new Error('No se pudo cambiar la contraseña');
    }
  },

  /**
   * Obtener configuración del tenant
   */
  async getTenantConfig(): Promise<any> {
    try {
      const response = await apiClient.get('/auth/tenant-config');
      return response.data;
    } catch (error) {
      throw new Error('No se pudo obtener la configuración del tenant');
    }
  },
};

export default authProfileApi;
